import { useCallback, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Loader2, Printer } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { docFormatDate, docFormatMoney, docT, type DocKey } from '../locales/documents'
import { documentFileName, vatSummary, type DocLine } from '../lib/documents'
import { STATUTORY_FALLBACK } from '../lib/cancellationTerms'
import { Button } from '../ui/kit'
import { useDocScale } from '../ui/useDocScale'
import { usePrintFileName } from '../ui/usePrintFileName'
import QuoteAcceptPanel from './QuoteAcceptPanel'
import InvoicePayPanel from './InvoicePayPanel'

interface PublicDoc {
  kind: 'quote' | 'invoice'
  number: string
  /** Der Stand des Dokuments: beim Angebot entscheidet er, ob noch zugesagt werden kann. */
  status: string
  language: string
  issue_date: string
  valid_until: string | null
  due_date: string | null
  currency: string
  totals: {
    net: number; vat: number; gross: number
    paid: number; prepayment: number; deposit: number
  }
  /** Tag und Weg der letzten Zahlung — macht aus der Rechnung eine Quittung. */
  paid_on: string | null
  paid_method: string | null
  intro: string | null
  terms: string | null
  lines: DocLine[]
  issuer: {
    name: string; address: string | null; vat_id: string | null
    email: string | null; phone: string | null
    iban: string | null; bic: string | null; holder: string | null
    accent: string | null; logo: string | null; vat_scheme: string
  }
  recipient: {
    name: string | null; address: string | null
    postcode: string | null; city: string | null; country: string | null
  }
  stay: {
    unit: string; property: string | null
    arrival: string; departure: string
    adults: number; children: number
  } | null
  /**
   * Nur beim Angebot: was der Gast selbst schon entschieden hat und wie er zahlen
   * kann. Auf einer Rechnung ist der Block `null` — dort gibt es nichts anzunehmen.
   */
  acceptance: {
    accepted_at: string | null
    declined_at: string | null
    prepayment_due: string | null
    prepaid: boolean
    online_payment: boolean
  } | null
  /**
   * Das Gegenstück nur bei der Rechnung: ob sie online bezahlt werden darf. Ein
   * einziges Flag, weil es an `tenants.stripe_*` hängt — Spalten, die der Gast nie zu
   * sehen bekommt. Beim Angebot ist der Block `null`.
   */
  payment: { online_payment: boolean } | null
}

/**
 * The quote or invoice as the guest sees it, opened from the link in their email.
 *
 * The token is the only credential, so the page is deliberately read-only and the
 * server function returns just the document — no internal notes, no other bookings.
 * Rendered with the same stylesheet as the landlord's copy, so what the guest prints
 * is the document the landlord approved.
 */
export default function PublicDocumentView() {
  const { token } = useParams<{ token: string }>()
  // /q/<token> is a quote, /i/<token> an invoice — the prefix is the discriminator.
  const kind = useLocation().pathname.startsWith('/i/') ? 'i' : 'q'
  const [doc, setDoc] = useState<PublicDoc | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'missing'>('loading')
  const { viewportRef, sheetRef } = useDocScale<HTMLDivElement, HTMLElement>([doc])

  // Auch der Gast speichert diesen Beleg irgendwo ab — mit demselben Namen, den der
  // Vermieter in seinem Ordner hat.
  usePrintFileName(doc ? documentFileName({
    kind: doc.kind,
    lang: doc.language === 'en' ? 'en' : 'de',
    number: doc.number,
    issueDate: doc.issue_date,
  }) : null)

  /**
   * Frisch laden. Nach jeder Entscheidung des Gasts noch einmal, denn dann stimmt
   * der Stand auf dem Bildschirm nicht mehr — und im Fall „Zeitraum inzwischen
   * vergeben" ist die Wahrheit eine andere als die, die er vor sich hatte.
   */
  const load = useCallback(async () => {
    if (!supabase || !token) { setState('missing'); return }
    const docKind = kind === 'i' ? 'invoice' : 'quote'
    const { data, error } = await supabase.rpc('public_document', {
      p_kind: docKind, p_token: token,
    })
    if (error || !data) { setState('missing'); return }
    setDoc(data as PublicDoc)
    setState('ready')
  }, [kind, token])

  useEffect(() => {
    const docKind = kind === 'i' ? 'invoice' : 'quote'
    void (async () => {
      await load()
      if (supabase) {
        /*
         * DAS `.then()` IST NICHT ZIERRAT — ohne es geht der Aufruf NIE hinaus.
         *
         * Der Abfragebauer von supabase-js ist lazy: er schickt die Anfrage erst,
         * wenn jemand `then` aufruft (per `await` oder `.then()`). `void` tut
         * genau das Gegenteil — es verwirft den Ausdruck, ohne ihn auszulösen.
         * Hier stand bis zum 28.07.2026 ein blankes `void supabase.rpc(...)`, und
         * die Folge war unsichtbar: `viewed_at` wurde nie gesetzt, und der
         * Vermieter las an seinem Angebot für immer „Noch nicht geöffnet" —
         * obwohl der Gast es längst gelesen hatte. Aufgefallen beim ersten
         * Durchlauf der ganzen Kette; die Funktion selbst war immer in Ordnung.
         *
         * Die fünf anderen `void supabase.…`-Stellen im Projekt hängen ein
         * `.then()` an und sind deshalb in Ordnung. Wer hier eines entfernt,
         * schaltet die Funktion stumm ab.
         */
        void supabase.rpc('public_document_seen', { p_kind: docKind, p_token: token })
          .then(({ error }) => {
            // Nur ins Protokoll: dass der Gast gesehen hat, ist eine Randnotiz
            // für den Vermieter und kein Grund, dem Gast etwas anzuzeigen.
            if (error) console.error('[StaySuite] document_seen:', error.message)
          })
      }
    })()
  }, [kind, token, load])

  if (state === 'loading') {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted gap-2">
        <Loader2 size={18} className="animate-spin" />
      </div>
    )
  }

  if (state === 'missing' || !doc) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-6 text-center">
        <p className="text-muted">
          Dieses Dokument ist nicht (mehr) verfügbar.<br />
          <span className="text-sm">This document is no longer available.</span>
        </p>
      </div>
    )
  }

  const L = doc.language === 'en' ? 'en' : 'de'
  const t = (k: DocKey) => docT(L, k)
  const money = (n: number) => docFormatMoney(L, n, doc.currency)
  const date = (iso: string) => docFormatDate(L, iso)
  const buckets = vatSummary(doc.lines)
  const outstanding = Math.round((doc.totals.gross - doc.totals.paid) * 100) / 100
  // Eine bezahlte Rechnung darf nicht zum zweiten Mal zum Überweisen auffordern.
  const settled = doc.kind === 'invoice' && doc.totals.paid > 0 && outstanding <= 0.004

  return (
    <div className="min-h-dvh">
      <div className="no-print sticky top-0 z-10 bg-surface border-b border-border
                      px-4 py-3 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {t(doc.kind)} {doc.number}
          </div>
          <div className="text-xs text-muted truncate">{doc.issuer.name}</div>
        </div>
        <Button variant="primary" size="sm" onClick={() => window.print()}>
          <Printer size={16} /> PDF
        </Button>
      </div>

      <div className="p-4 no-print-padding doc-viewport" ref={viewportRef}>
        <article className="doc-sheet" ref={sheetRef}>
          <header className="doc-head">
            <div>
              {doc.issuer.logo && (
                <img className="doc-logo" src={doc.issuer.logo} alt="" />
              )}
              <div className="doc-issuer-name">{doc.issuer.name}</div>
              {doc.issuer.address && <div>{doc.issuer.address}</div>}
              {doc.issuer.vat_id && <div>{t('vatId')}: {doc.issuer.vat_id}</div>}
              {doc.issuer.email && <div>{doc.issuer.email}</div>}
            </div>
            <div className="doc-title-block">
              <h1>{t(doc.kind)}</h1>
              <table>
                <tbody>
                  <tr><td>{t('number')}</td><td>{doc.number}</td></tr>
                  <tr><td>{t('issueDate')}</td><td>{date(doc.issue_date)}</td></tr>
                  {doc.due_date && (
                    <tr><td>{t('dueDate')}</td><td>{date(doc.due_date)}</td></tr>
                  )}
                  {doc.valid_until && (
                    <tr><td>{t('validUntil')}</td><td>{date(doc.valid_until)}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </header>

          <section className="doc-recipient">
            <div className="doc-recipient-name">{doc.recipient.name ?? '—'}</div>
            {doc.recipient.address && <div>{doc.recipient.address}</div>}
            {(doc.recipient.postcode || doc.recipient.city) && (
              <div>{[doc.recipient.postcode, doc.recipient.city].filter(Boolean).join(' ')}</div>
            )}
            {doc.recipient.country && <div>{doc.recipient.country}</div>}
          </section>

          <p className="doc-intro">
            {doc.intro ?? (doc.kind === 'quote' ? t('quoteIntro') : t('invoiceThanks'))}
          </p>

          {doc.stay && (
            <section className="doc-stay">
              <div>
                <span>{t('unit')}</span>
                <strong>
                  {doc.stay.unit}{doc.stay.property && ` · ${doc.stay.property}`}
                </strong>
              </div>
              <div>
                <span>{t('stay')}</span>
                <strong>{date(doc.stay.arrival)} – {date(doc.stay.departure)}</strong>
              </div>
              <div>
                <span>{t('guests')}</span>
                <strong>
                  {doc.stay.adults} {t('adults')}
                  {doc.stay.children > 0 && `, ${doc.stay.children} ${t('children')}`}
                </strong>
              </div>
            </section>
          )}

          <table className="doc-lines">
            <thead>
              <tr>
                <th className="l">{t('position')}</th>
                <th className="r">{t('qty')}</th>
                <th className="r">{t('unitPrice')}</th>
                <th className="r">{t('vat')}</th>
                <th className="r">{t('gross')}</th>
              </tr>
            </thead>
            <tbody>
              {doc.lines.map((l, i) => (
                <tr key={i}>
                  <td className="l">{l.label}</td>
                  <td className="r">{l.qty}</td>
                  <td className="r">{money(l.unitPrice)}</td>
                  <td className="r">{l.vatRate > 0 ? `${l.vatRate} %` : '—'}</td>
                  <td className="r">{money(l.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <section className="doc-totals">
            <table className="doc-vat">
              <caption>{t('vatSummary')}</caption>
              <thead>
                <tr>
                  <th className="l">{t('rate')}</th>
                  <th className="r">{t('net')}</th>
                  <th className="r">{t('vat')}</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b) => (
                  <tr key={b.rate}>
                    <td className="l">{b.rate} %</td>
                    <td className="r">{money(b.net)}</td>
                    <td className="r">{money(b.vat)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <table className="doc-sum">
              <tbody>
                <tr><td>{t('totalNet')}</td><td className="r">{money(doc.totals.net)}</td></tr>
                <tr><td>{t('totalVat')}</td><td className="r">{money(doc.totals.vat)}</td></tr>
                <tr className="grand">
                  <td>{t('totalGross')}</td>
                  <td className="r">{money(doc.totals.gross)}</td>
                </tr>
                {doc.totals.paid > 0 && (
                  <>
                    {/* Die Anzahlung zuerst und benannt: sie ist der Betrag, an den
                        sich der Gast erinnert, und wenn er ihn nicht wiederfindet,
                        wirkt die Rechnung, als würde sie ihn zweimal verlangen. */}
                    {doc.kind === 'invoice' && doc.totals.prepayment > 0 && (
                      <tr>
                        <td>{t('prepaymentReceived')}</td>
                        <td className="r">−{money(doc.totals.prepayment)}</td>
                      </tr>
                    )}
                    {doc.totals.paid
                      - (doc.kind === 'invoice' ? doc.totals.prepayment : 0) > 0 && (
                      <tr>
                        <td>{t('paid')}</td>
                        <td className="r">
                          −{money(doc.totals.paid
                            - (doc.kind === 'invoice' ? doc.totals.prepayment : 0))}
                        </td>
                      </tr>
                    )}
                    <tr className="grand">
                      <td>{t('outstanding')}</td>
                      <td className="r">{money(outstanding)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </section>

          {/* Dieselbe Quittungszeile wie beim Vermieter: der Gast behält diese
              Fassung, und sie muss ohne Rückfrage belegen, dass er bezahlt hat. */}
          {doc.kind === 'invoice' && outstanding <= 0.004 && doc.totals.paid > 0
            && doc.paid_on && (
            <p className="doc-paid">
              {t('paidInFull')} {date(doc.paid_on)}
              {doc.paid_method && t(`payMethod_${doc.paid_method}` as DocKey)
                && ` · ${t(`payMethod_${doc.paid_method}` as DocKey)}`}
            </p>
          )}

          {doc.issuer.vat_scheme !== 'standard' && (
            <p className="doc-note">{t('smallBusiness')}</p>
          )}

          {/* Bei einer bezahlten Rechnung ohne Kaution bleibt hier nichts zu sagen —
              dann entfällt der ganze Abschnitt statt leer dazustehen. */}
          {(!settled || doc.totals.deposit > 0 || doc.kind === 'quote') && (
          <section className="doc-payment">
            <h2>{t('payment')}</h2>
            {/* Auf einem Angebot ist die Anzahlung eine Aufforderung, auf einer
                Rechnung eine Gutschrift — sie steht dort schon oben in den Summen. */}
            {doc.kind !== 'invoice' && doc.totals.prepayment > 0 && (
              <p><strong>{t('prepayment')}: {money(doc.totals.prepayment)}</strong></p>
            )}
            {doc.issuer.iban && !settled && (
              <>
                <p>{t('bankTransfer')}</p>
                <table className="doc-bank">
                  <tbody>
                    {doc.issuer.holder && (
                      <tr><td>{t('accountHolder')}</td><td>{doc.issuer.holder}</td></tr>
                    )}
                    <tr><td>IBAN</td><td>{doc.issuer.iban}</td></tr>
                    {doc.issuer.bic && <tr><td>BIC</td><td>{doc.issuer.bic}</td></tr>}
                    <tr><td>{t('reference')}</td><td>{doc.number}</td></tr>
                  </tbody>
                </table>
              </>
            )}
            {doc.totals.deposit > 0 && (
              <p className="doc-note">
                <strong>{t('deposit')}: {money(doc.totals.deposit)}</strong>
                {' — '}{t('depositNote')}
              </p>
            )}
            {doc.kind === 'quote' && (
              <>
                <p className="doc-note">{t('quoteTerms')}</p>
                <p className="doc-note whitespace-pre-line">
                  {doc.terms?.trim() || STATUTORY_FALLBACK[L]}
                </p>
              </>
            )}
          </section>
          )}
        </article>
      </div>

      {/*
        Erst das Angebot, dann die Entscheidung. Umgekehrt stünde der Knopf über dem,
        wozu er ja sagt — und der Gast soll gelesen haben, was er annimmt.
        Nur beim Angebot: eine Rechnung nimmt man nicht an, man bezahlt sie.
      */}
      {doc.kind === 'quote' && token && (
        <QuoteAcceptPanel doc={doc} token={token} lang={L} onChanged={() => { void load() }} />
      )}

      {/*
        Und dasselbe für die Rechnung: erst der Beleg, dann der Knopf. Die Fläche
        entscheidet selbst, ob sie überhaupt erscheint — ohne verbundenes Stripe-Konto
        bleibt es bei der Bankverbindung, die oben auf dem Blatt steht.
      */}
      {doc.kind === 'invoice' && token && (
        <InvoicePayPanel doc={doc} token={token} lang={L} onChanged={() => { void load() }} />
      )}
    </div>
  )
}
